import React, { useEffect, useState } from "react";
import {
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "../../hooks";
import { showSnackbar } from "../../reducers/snackbarSlice";
import { getPrompt, createPromptVersion, PromptDetail, PromptVersion } from "./PromptsRepository";

const PromptEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { name } = useParams<{ name: string }>();

  const [detail, setDetail] = useState<PromptDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [role, setRole] = useState<string>("");
  const [prompt, setPrompt] = useState<string>("");

  const loadDetail = React.useCallback(
    (prefill: boolean) => {
      if (!name) return Promise.resolve();
      return getPrompt(name)
        .then((data) => {
          setDetail(data);
          if (prefill) {
            const current = data.versions[0];
            setRole(current?.role ?? "");
            setPrompt(current?.prompt ?? "");
          }
        })
        .catch((error) => {
          console.error(error);
          dispatch(showSnackbar({ message: t("prompts.load_failed"), severity: "error" }));
        });
    },
    [name, dispatch, t],
  );

  useEffect(() => {
    let active = true;
    loadDetail(true).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [loadDetail]);

  const handleSave = async () => {
    if (!name) return;
    setSaving(true);
    try {
      const created = await createPromptVersion(name, prompt, role);
      dispatch(showSnackbar({ message: t("prompts.saved", { version: created.version }), severity: "success" }));
      await loadDetail(false);
    } catch (error) {
      console.error(error);
      dispatch(showSnackbar({ message: t("prompts.save_failed"), severity: "error" }));
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = (version: PromptVersion) => {
    setRole(version.role);
    setPrompt(version.prompt);
    dispatch(showSnackbar({ message: t("prompts.restored", { version: version.version }), severity: "info" }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const versions = detail?.versions ?? [];

  return (
    <Box data-cy="prompt-editor-page">
      <Button onClick={() => navigate("/prompts")} sx={{ mb: 2 }} data-cy="back-to-prompts">
        {t("generic.back")}
      </Button>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {t("prompts.editor_title")}: {name}
      </Typography>

      <Box display="flex" gap={3} flexDirection={{ xs: "column", md: "row" }}>
        <Paper elevation={2} sx={{ p: 3, flex: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("prompts.save_warning")}
          </Alert>
          <TextField
            label={t("prompts.role_label")}
            fullWidth
            multiline
            minRows={2}
            value={role}
            onChange={(e) => setRole(e.target.value)}
            sx={{ mb: 2 }}
            data-cy="prompt-role"
          />
          <TextField
            label={t("prompts.prompt_label")}
            fullWidth
            multiline
            minRows={10}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            sx={{ mb: 2 }}
            data-cy="prompt-text"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            data-cy="save-prompt-version"
          >
            {t("prompts.save_new_version")}
            {saving && <CircularProgress size={20} sx={{ ml: 2 }} />}
          </Button>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, flex: 1 }} data-cy="version-history">
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t("prompts.version_history")}
          </Typography>
          {versions.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              {t("prompts.no_versions")}
            </Typography>
          ) : (
            <List dense>
              {versions.map((version, index) => (
                <React.Fragment key={version.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem
                    secondaryAction={
                      <Button size="small" onClick={() => handleRestore(version)} data-cy="restore-version">
                        {t("prompts.restore")}
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={`${t("prompts.version")} ${version.version}`}
                      secondary={version.prompt.slice(0, 80)}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default PromptEditorPage;
